import { useState, useEffect, useRef } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useQuery } from '@tanstack/react-query'
import { Settings2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Badge } from '@/components/ui/badge'
import { ProcessingStatusCard } from '@/components/StoreModels/ProcessingStatusCard'
import {
  GROUPING_METHOD_BADGE_VARIANT,
  GROUPING_METHOD_LABEL,
} from '@/components/StoreModels/constants'
import { ManualGroupingDialog } from './ManualGroupingDialog'
import { getStoreModelStores } from '@/api/storeModels'
import { queryKeys } from '@/lib/queryKeys'
import { useApplyGrouping } from '@/hooks/useStoreModels'
import { useStoreAttributes } from '@/hooks/useStoreAttributes'
import { useProcessingStatus } from '@/hooks/useProcessingStatus'
import { toast } from '@/stores/useToastStore'
import type { AxiosError } from 'axios'
import type {
  StoreModelDetailRead,
  GroupingMethod,
  ProcessingResponse,
  ApplyGroupingRequest,
} from '@/types'

const BREAKPOINT_PRESETS = [
  { value: '33, 66', label: 'Tertiles (33, 66)' },
  { value: '25, 50, 75', label: 'Quartiles (25, 50, 75)' },
  { value: '20, 40, 60, 80', label: 'Quintiles (20, 40, 60, 80)' },
  { value: 'custom', label: 'Custom' },
]

const CUMULATIVE_PRESETS = [
  { value: '33, 66', label: '3 Bands (33, 66)' },
  { value: '20, 50, 80', label: '4 Bands (20, 50, 80)' },
  { value: '20, 40, 60, 80', label: '5 Bands (20, 40, 60, 80)' },
  { value: 'custom', label: 'Custom' },
]

const formSchema = z
  .object({
    method: z.enum([
      'K_MEANS',
      'PERCENTILE',
      'CUMULATIVE_CONTRIBUTION',
      'ATTRIBUTE',
      'MANUAL',
      'POG_VERSION',
    ] as const),
    number_of_clusters: z.coerce.number().optional(),
    metric: z.string().optional(),
    breakpoints_preset: z.string().optional(),
    breakpoints: z.string().optional(),
    cumulative_thresholds_preset: z.string().optional(),
    cumulative_thresholds: z.string().optional(),
    attribute_name: z.string().optional(),
    groups_json: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.method === 'K_MEANS') {
      if (
        data.number_of_clusters == null ||
        data.number_of_clusters < 2 ||
        data.number_of_clusters > 20
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Number of clusters must be between 2 and 20',
          path: ['number_of_clusters'],
        })
      }
    }
    if (data.method === 'PERCENTILE') {
      if (!data.metric) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Metric is required',
          path: ['metric'],
        })
      }
      if (!data.breakpoints_preset) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Please select a breakpoint preset',
          path: ['breakpoints_preset'],
        })
      }
      const resolved =
        data.breakpoints_preset === 'custom'
          ? data.breakpoints
          : data.breakpoints_preset
      if (!resolved?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Breakpoints are required',
          path: ['breakpoints'],
        })
      } else {
        const parts = resolved.split(',').map(s => s.trim())
        const invalid = parts.some(p => isNaN(Number(p)) || p === '')
        if (invalid) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Breakpoints must be comma-separated numbers',
            path: ['breakpoints'],
          })
        }
      }
    }
    if (data.method === 'CUMULATIVE_CONTRIBUTION') {
      if (!data.metric) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Metric is required',
          path: ['metric'],
        })
      }
      if (!data.cumulative_thresholds_preset) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Please select a threshold preset',
          path: ['cumulative_thresholds_preset'],
        })
      }
      const resolved =
        data.cumulative_thresholds_preset === 'custom'
          ? data.cumulative_thresholds
          : data.cumulative_thresholds_preset
      if (!resolved?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Thresholds are required',
          path: ['cumulative_thresholds'],
        })
      } else {
        const parts = resolved.split(',').map(s => s.trim())
        const nums = parts.map(Number)
        if (parts.some(p => isNaN(Number(p)) || p === '')) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Thresholds must be comma-separated numbers',
            path: ['cumulative_thresholds'],
          })
        } else if (nums.some(n => n <= 0 || n >= 100)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Each threshold must be between 0 and 100 (exclusive)',
            path: ['cumulative_thresholds'],
          })
        }
      }
    }
    if (data.method === 'ATTRIBUTE') {
      if (!data.attribute_name?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Attribute name is required',
          path: ['attribute_name'],
        })
      }
    }
    // MANUAL validation is handled in onSubmit via manualGroups state
  })

type FormValues = z.infer<typeof formSchema>

interface GroupingTabProps {
  model: StoreModelDetailRead
  onGroupingApplied: () => void
}

function getDefaultValues(model: StoreModelDetailRead): FormValues {
  const method: GroupingMethod = model.grouping_method ?? 'K_MEANS'
  const config = model.grouping_config ?? {}

  const base: FormValues = {
    method,
    number_of_clusters: 3,
    metric: 'SALES_VOLUME',
    breakpoints_preset: '33, 66',
    breakpoints: '',
    cumulative_thresholds_preset: '20, 40, 60, 80',
    cumulative_thresholds: '',
    attribute_name: 'region',
    groups_json: '',
  }

  if (method === 'K_MEANS' && config.number_of_clusters != null) {
    base.number_of_clusters = config.number_of_clusters as number
  }
  if (method === 'PERCENTILE') {
    if (config.metric) base.metric = config.metric as string
    if (Array.isArray(config.breakpoints)) {
      const saved = (config.breakpoints as number[]).join(', ')
      const isPreset = BREAKPOINT_PRESETS.some(p => p.value === saved)
      base.breakpoints_preset = isPreset ? saved : 'custom'
      base.breakpoints = isPreset ? '' : saved
    }
  }
  if (method === 'CUMULATIVE_CONTRIBUTION') {
    if (config.metric) base.metric = config.metric as string
    if (Array.isArray(config.thresholds)) {
      const saved = (config.thresholds as number[]).join(', ')
      const isPreset = CUMULATIVE_PRESETS.some(p => p.value === saved)
      base.cumulative_thresholds_preset = isPreset ? saved : 'custom'
      base.cumulative_thresholds = isPreset ? '' : saved
    }
  }
  if (method === 'ATTRIBUTE' && config.attribute_name) {
    base.attribute_name = config.attribute_name as string
  }
  // MANUAL groups are managed via manualGroups state, not the form
  return base
}

export function GroupingTab({ model, onGroupingApplied }: GroupingTabProps) {
  const applyMutation = useApplyGrouping(model.id)
  const { data: storeAttrsData, isLoading: attrsLoading } = useStoreAttributes()
  const groupableAttributes = (storeAttrsData?.attributes ?? []).filter(
    a => a.groupable
  )

  // Manual grouping state
  const [manualDialogOpen, setManualDialogOpen] = useState(false)
  const [manualGroups, setManualGroups] = useState<
    { name: string; store_ids: string[] }[]
  >(() => {
    if (model.grouping_method === 'MANUAL' && model.grouping_config?.groups) {
      return model.grouping_config.groups as {
        name: string
        store_ids: string[]
      }[]
    }
    return []
  })

  // Fetch all members for manual grouping dialog
  const { data: allMembers, isLoading: membersLoading } = useQuery({
    queryKey: [...queryKeys.storeModels.stores(model.id, {}), 'all'],
    queryFn: async () => {
      const PAGE_SIZE = 200
      const first = await getStoreModelStores({
        modelId: model.id,
        skip: 0,
        limit: PAGE_SIZE,
      })
      const all = [...first.data]
      const total = first.pagination.total
      const remaining = Math.ceil((total - PAGE_SIZE) / PAGE_SIZE)
      const pages = await Promise.all(
        Array.from({ length: Math.max(0, remaining) }, (_, i) =>
          getStoreModelStores({
            modelId: model.id,
            skip: (i + 1) * PAGE_SIZE,
            limit: PAGE_SIZE,
          })
        )
      )
      for (const page of pages) all.push(...page.data)
      return all
    },
    enabled: manualDialogOpen,
    staleTime: 60_000,
  })

  const [processingResponse, setProcessingResponse] =
    useState<ProcessingResponse | null>(null)

  const processingStatus = useProcessingStatus(processingResponse)
  const statusCardRef = useRef<HTMLDivElement>(null)

  // Scroll to the processing status card when processing starts
  useEffect(() => {
    if (processingStatus.state !== 'idle') {
      statusCardRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      })
    }
  }, [processingStatus.state])

  const isFormDisabled =
    applyMutation.isPending || processingStatus.state === 'processing'

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: getDefaultValues(model),
  })

  // Reset form when model changes
  useEffect(() => {
    reset(getDefaultValues(model))
  }, [model, reset])

  // When processing completes, trigger callback then clear response
  useEffect(() => {
    if (processingStatus.state === 'complete') {
      onGroupingApplied()
      setProcessingResponse(null)
      processingStatus.reset()
    } else if (processingStatus.state === 'error') {
      setProcessingResponse(null)
      processingStatus.reset()
    }
  }, [processingStatus.state]) // eslint-disable-line react-hooks/exhaustive-deps

  const currentMethod = watch('method')

  const handleMethodChange = (value: GroupingMethod) => {
    const defaults = getDefaultValues(model)
    reset({ ...defaults, method: value })
  }

  const onSubmit = (data: FormValues) => {
    let config: Record<string, unknown> | undefined

    switch (data.method) {
      case 'K_MEANS':
        config = { number_of_clusters: data.number_of_clusters }
        break
      case 'PERCENTILE': {
        const resolved =
          data.breakpoints_preset === 'custom'
            ? data.breakpoints!
            : data.breakpoints_preset!
        config = {
          metric: data.metric,
          breakpoints: resolved.split(',').map(s => Number(s.trim())),
        }
        break
      }
      case 'CUMULATIVE_CONTRIBUTION': {
        const resolved =
          data.cumulative_thresholds_preset === 'custom'
            ? data.cumulative_thresholds!
            : data.cumulative_thresholds_preset!
        config = {
          metric: data.metric,
          thresholds: resolved.split(',').map(s => Number(s.trim())),
        }
        break
      }
      case 'ATTRIBUTE':
        config = { attribute_name: data.attribute_name }
        break
      case 'MANUAL':
        if (manualGroups.length === 0) {
          toast.error('Please configure groups before applying')
          return
        }
        config = { groups: manualGroups }
        break
      case 'POG_VERSION':
        config = { pog_group: model.criteria?.pog_group }
        break
    }

    const payload: ApplyGroupingRequest = {
      method: data.method,
      config,
    }

    applyMutation.mutate(payload, {
      onSuccess: response => {
        setProcessingResponse(response)
        toast.success('Grouping started')
      },
      onError: error => {
        const axiosError = error as AxiosError<{ detail?: string }>
        const message =
          axiosError.response?.data?.detail ?? 'Failed to apply grouping'
        toast.error(message)
      },
    })
  }

  return (
    <div className="flex flex-col gap-6">
      <div ref={statusCardRef}>
        <ProcessingStatusCard status={processingStatus} />
      </div>

      {/* Current grouping info */}
      {model.grouping_method && (
        <Card>
          <CardHeader>
            <CardTitle>Current Grouping</CardTitle>
          </CardHeader>
          <CardContent className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Method:</span>
              <Badge
                variant={GROUPING_METHOD_BADGE_VARIANT[model.grouping_method]}
              >
                {GROUPING_METHOD_LABEL[model.grouping_method]}
              </Badge>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Groups:</span>
              <span className="text-sm font-medium">{model.group_count}</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Grouping form */}
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Apply Grouping</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            {/* Method selector */}
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="grouping-method">Method *</Label>
              <Select
                value={currentMethod}
                onValueChange={handleMethodChange}
                disabled={isFormDisabled}
              >
                <SelectTrigger id="grouping-method">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="K_MEANS">K-Means</SelectItem>
                  <SelectItem value="PERCENTILE">Percentile</SelectItem>
                  <SelectItem value="CUMULATIVE_CONTRIBUTION">
                    Cumulative Contribution
                  </SelectItem>
                  <SelectItem value="ATTRIBUTE">Attribute</SelectItem>
                  <SelectItem value="MANUAL">Manual</SelectItem>
                  {model.criteria?.pog_group && (
                    <SelectItem value="POG_VERSION">POG Version</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>

            {/* K_MEANS config */}
            {currentMethod === 'K_MEANS' && (
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="number_of_clusters">
                  Number of Clusters (2-20) *
                </Label>
                <Input
                  id="number_of_clusters"
                  type="number"
                  min={2}
                  max={20}
                  disabled={isFormDisabled}
                  {...register('number_of_clusters')}
                />
                {errors.number_of_clusters && (
                  <p className="text-sm text-destructive">
                    {errors.number_of_clusters.message}
                  </p>
                )}
              </div>
            )}

            {/* PERCENTILE config */}
            {currentMethod === 'PERCENTILE' && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="metric">Metric *</Label>
                  <Select
                    value={watch('metric') ?? 'SALES_VOLUME'}
                    onValueChange={v => setValue('metric', v)}
                    disabled={isFormDisabled}
                  >
                    <SelectTrigger id="metric">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="SALES_VOLUME">Sales Volume</SelectItem>
                      <SelectItem value="UNITS">Units</SelectItem>
                      <SelectItem value="GM_PERCENT">Gross Margin %</SelectItem>
                    </SelectContent>
                  </Select>
                  {errors.metric && (
                    <p className="text-sm text-destructive">
                      {errors.metric.message}
                    </p>
                  )}
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="breakpoints_preset">Breakpoints *</Label>
                  <Select
                    value={watch('breakpoints_preset') ?? ''}
                    onValueChange={v => {
                      setValue('breakpoints_preset', v)
                      if (v !== 'custom') setValue('breakpoints', '')
                    }}
                    disabled={isFormDisabled}
                  >
                    <SelectTrigger id="breakpoints_preset">
                      <SelectValue placeholder="Select breakpoints..." />
                    </SelectTrigger>
                    <SelectContent>
                      {BREAKPOINT_PRESETS.map(p => (
                        <SelectItem key={p.value} value={p.value}>
                          {p.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.breakpoints_preset && (
                    <p className="text-sm text-destructive">
                      {errors.breakpoints_preset.message}
                    </p>
                  )}
                  {watch('breakpoints_preset') === 'custom' && (
                    <>
                      <Input
                        id="breakpoints"
                        placeholder="e.g. 25, 50, 75"
                        disabled={isFormDisabled}
                        {...register('breakpoints')}
                      />
                      {errors.breakpoints && (
                        <p className="text-sm text-destructive">
                          {errors.breakpoints.message}
                        </p>
                      )}
                    </>
                  )}
                </div>
              </div>
            )}

            {/* CUMULATIVE_CONTRIBUTION config */}
            {currentMethod === 'CUMULATIVE_CONTRIBUTION' && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="cumulative_metric">Metric *</Label>
                  <Select
                    value={watch('metric') ?? 'SALES_VOLUME'}
                    onValueChange={v => setValue('metric', v)}
                    disabled={isFormDisabled}
                  >
                    <SelectTrigger id="cumulative_metric">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="SALES_VOLUME">Sales Volume</SelectItem>
                      <SelectItem value="UNITS">Units</SelectItem>
                      <SelectItem value="GM_PERCENT">Gross Margin %</SelectItem>
                    </SelectContent>
                  </Select>
                  {errors.metric && (
                    <p className="text-sm text-destructive">
                      {errors.metric.message}
                    </p>
                  )}
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="cumulative_thresholds_preset">
                    Thresholds *
                  </Label>
                  <Select
                    value={watch('cumulative_thresholds_preset') ?? ''}
                    onValueChange={v => {
                      setValue('cumulative_thresholds_preset', v)
                      if (v !== 'custom') setValue('cumulative_thresholds', '')
                    }}
                    disabled={isFormDisabled}
                  >
                    <SelectTrigger id="cumulative_thresholds_preset">
                      <SelectValue placeholder="Select thresholds..." />
                    </SelectTrigger>
                    <SelectContent>
                      {CUMULATIVE_PRESETS.map(p => (
                        <SelectItem key={p.value} value={p.value}>
                          {p.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.cumulative_thresholds_preset && (
                    <p className="text-sm text-destructive">
                      {errors.cumulative_thresholds_preset.message}
                    </p>
                  )}
                  {watch('cumulative_thresholds_preset') === 'custom' && (
                    <>
                      <Input
                        id="cumulative_thresholds"
                        placeholder="e.g. 20, 50, 80"
                        disabled={isFormDisabled}
                        {...register('cumulative_thresholds')}
                      />
                      {errors.cumulative_thresholds && (
                        <p className="text-sm text-destructive">
                          {errors.cumulative_thresholds.message}
                        </p>
                      )}
                    </>
                  )}
                </div>
              </div>
            )}

            {/* ATTRIBUTE config */}
            {currentMethod === 'ATTRIBUTE' && (
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="attribute_name">Attribute Name *</Label>
                <Select
                  value={watch('attribute_name') ?? ''}
                  onValueChange={v => setValue('attribute_name', v)}
                  disabled={isFormDisabled || attrsLoading}
                >
                  <SelectTrigger id="attribute_name">
                    <SelectValue
                      placeholder={
                        attrsLoading
                          ? 'Loading attributes...'
                          : 'Select an attribute'
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {groupableAttributes.map(attr => (
                      <SelectItem key={attr.name} value={attr.name}>
                        {attr.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.attribute_name && (
                  <p className="text-sm text-destructive">
                    {errors.attribute_name.message}
                  </p>
                )}
              </div>
            )}

            {/* MANUAL config */}
            {currentMethod === 'MANUAL' && (
              <div className="flex flex-col gap-3">
                <div className="flex items-center justify-between">
                  <Label>Store Groups</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="gap-1.5 cursor-pointer"
                    disabled={isFormDisabled}
                    onClick={() => setManualDialogOpen(true)}
                  >
                    <Settings2 className="h-3.5 w-3.5" />
                    Configure Groups
                  </Button>
                </div>
                {manualGroups.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {manualGroups.map(g => (
                      <Badge key={g.name} variant="info" className="text-xs">
                        {g.name}: {g.store_ids.length} stores
                      </Badge>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    No groups configured. Click &quot;Configure Groups&quot; to
                    assign stores.
                  </p>
                )}
                <ManualGroupingDialog
                  open={manualDialogOpen}
                  onOpenChange={setManualDialogOpen}
                  members={allMembers ?? []}
                  loading={membersLoading}
                  initialGroups={manualGroups}
                  onSave={setManualGroups}
                />
              </div>
            )}

            {/* POG_VERSION config */}
            {currentMethod === 'POG_VERSION' && (
              <div className="flex flex-col gap-1.5">
                <Label>POG Group</Label>
                <p className="text-sm text-muted-foreground">
                  Stores will be grouped by their POG version within{' '}
                  <span className="font-medium text-foreground">
                    {model.criteria?.pog_group}
                  </span>
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex items-center justify-end gap-3">
          <Button
            type="submit"
            className="cursor-pointer"
            disabled={isFormDisabled}
          >
            {applyMutation.isPending ? 'Applying...' : 'Apply Grouping'}
          </Button>
        </div>
      </form>
    </div>
  )
}
